import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Pagination,
  CircularProgress,
  Paper,
  Chip,
  Button,
  Tooltip,
  Skeleton,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import SearchOff from '@mui/icons-material/SearchOff';
import { useTranslation } from 'react-i18next';
import useProductData from '../../hooks/useProductData';
import verticalConfigs from './verticalConfigs';
import PersonaFilterBar from './PersonaFilterBar';
import ProductRow from './ProductRow';
import ProductDetailModal from './ProductDetailModal';
import ProductComparisonModal from './ProductComparisonModal';
import ComparatorTray from './ComparatorTray';
import UniversalScoreBadge from './UniversalScoreBadge';
import WhyBullets from './WhyBullets';
import TrustMicroline from './TrustMicroline';
import TrustHeroBar from './TrustHeroBar';
import ScoringExplainerModal from './ScoringExplainerModal';
import { getLogoPath } from './logoMap';
import { getVerticalFreshnessDate } from './freshness';
import * as analytics from './analytics';

const ITEMS_PER_PAGE = 12;

/**
 * Affiliate tiebreaker for score-based sorting.
 * Within ≤3 point difference, prefer items with apply_url.
 * Does NOT change displayed scores - sorting logic only.
 */
function applyAffiliateTiebreak(items, config) {
  return [...items].sort((a, b) => {
    const sa = a._n?.finmaple_score ?? -Infinity;
    const sb = b._n?.finmaple_score ?? -Infinity;
    // Within 3 point tie, affiliate-linked items surface first
    if (Math.abs(sa - sb) <= 3) {
      const aHas = !!config.getApplyUrl?.(a);
      const bHas = !!config.getApplyUrl?.(b);
      if (aHas && !bHas) return -1;
      if (!aHas && bHas) return 1;
    }
    return sb - sa;
  });
}

/**
 * Universal comparator orchestrator.
 * Usage: <FinmapleComparator category="bank_accounts" />
 */
const FinmapleComparator = ({ category }) => {
  const { t } = useTranslation();
  const config = verticalConfigs[category];
  const { items, rawSection, loading } = useProductData(config.dataPath, config.idField, config.key);

  // State
  const [activePersonas, setActivePersonas] = useState([]);
  const [sortKey, setSortKey] = useState(config.defaultSort);
  const [currentPage, setCurrentPage] = useState(1);
  const [detailItem, setDetailItem] = useState(null);
  const [selectedForCompare, setSelectedForCompare] = useState([]);
  const [showComparison, setShowComparison] = useState(false);
  const [showExplainer, setShowExplainer] = useState(false);

  // Analytics: page view
  useEffect(() => {
    if (!loading && items.length > 0) {
      analytics.trackView(category);
    }
  }, [loading, items.length, category]);

  // Global freshness
  const verticalFreshness = useMemo(
    () => getVerticalFreshnessDate(items, rawSection),
    [items, rawSection],
  );

  // Filter + sort + affiliate tiebreak
  const filtered = useMemo(() => {
    let result = items;

    // persona filter
    if (activePersonas.length > 0) {
      if (config.personaFilterFn) {
        result = result.filter((item) => config.personaFilterFn(item, activePersonas));
      } else {
        result = result.filter((item) => {
          const itemPersonas = item.ranking?.personas || [];
          return activePersonas.some((p) => itemPersonas.includes(p));
        });
      }
    }

    // sort
    if (config.sortFn && sortKey) {
      result = config.sortFn(result, sortKey);
    }

    // Affiliate tiebreak on score-based sorts only
    if (sortKey === 'finmaple_score') {
      result = applyAffiliateTiebreak(result, config);
    }

    return result;
  }, [items, activePersonas, sortKey, config]);

  // Recommendation line for #1
  const recommendationLine = useMemo(() => {
    if (filtered.length === 0 || !config.recommendationLine) return null;
    return config.recommendationLine(filtered[0]);
  }, [filtered, config]);

  // Pagination
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginatedItems = useMemo(
    () => filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE),
    [filtered, currentPage],
  );

  // Handlers
  const handlePersonaToggle = useCallback((value) => {
    analytics.trackFilterToggle(category, value);
    setActivePersonas((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
    setCurrentPage(1);
  }, [category]);

  const handleClearPersonas = useCallback(() => {
    setActivePersonas([]);
    setCurrentPage(1);
  }, []);

  const handleSortChange = useCallback((newSort) => {
    analytics.trackSortChange(category, newSort);
    setSortKey(newSort);
    setCurrentPage(1);
  }, [category]);

  const handleOpenDetail = useCallback((item) => {
    analytics.trackOpenDetail(category, item.id);
    setDetailItem(item);
  }, [category]);

  const toggleCompare = useCallback((item) => {
    setSelectedForCompare((prev) => {
      const exists = prev.some((c) => c.id === item.id);
      if (exists) return prev.filter((c) => c.id !== item.id);
      if (prev.length >= 4) return prev;
      analytics.trackAddCompare(category, item.id);
      return [...prev, item];
    });
  }, [category]);

  const handleRunCompare = useCallback(() => {
    analytics.trackRunCompare(category, selectedForCompare.length);
    setShowComparison(true);
  }, [category, selectedForCompare.length]);

  const handleExplainerOpen = useCallback(() => setShowExplainer(true), []);

  // ── TABLE LAYOUT (GICs) ──
  if (config.layoutMode === 'table') {
    return (
      <Box>
        <TrustHeroBar freshnessLabel={verticalFreshness.label} />
        <GlobalFreshnessLine freshness={verticalFreshness} loading={loading} />
        <GICTable data={items} rawSection={rawSection} loading={loading} config={config} />
        <TrustMicroline />
        <ScoringExplainerModal open={showExplainer} onClose={() => setShowExplainer(false)} />
      </Box>
    );
  }

  // ── CARDS LAYOUT (Registered Accounts) ──
  if (config.layoutMode === 'cards') {
    return (
      <Box>
        <TrustHeroBar freshnessLabel={verticalFreshness.label} />
        <GlobalFreshnessLine freshness={verticalFreshness} loading={loading} />
        <CardsLayout
          items={filtered}
          loading={loading}
          config={config}
          category={category}
          onViewDetail={handleOpenDetail}
          detailItem={detailItem}
          onCloseDetail={() => setDetailItem(null)}
          onExplainerClick={handleExplainerOpen}
        />
        <TrustMicroline />
        <ScoringExplainerModal open={showExplainer} onClose={() => setShowExplainer(false)} />
      </Box>
    );
  }

  // ── RANKED LAYOUT (default) ──
  if (loading) {
    return (
      <Box>
        <Skeleton variant="rounded" width={180} height={28} sx={{ mb: 2, borderRadius: '8px' }} />
        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" width={100} height={32} sx={{ borderRadius: '999px' }} />
          ))}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={120} sx={{ borderRadius: '14px' }} />
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Trust Hero Bar */}
      <TrustHeroBar freshnessLabel={verticalFreshness.label} />

      {/* Global freshness line */}
      <GlobalFreshnessLine freshness={verticalFreshness} loading={loading} />

      {/* Count + Sort header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
          {filtered.length} {config.countLabel}
        </Typography>

        {config.sortOptions.length > 0 && (
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 } }}>
            <Select
              value={sortKey}
              onChange={(e) => handleSortChange(e.target.value)}
              sx={{ fontSize: '0.85rem', minHeight: 44 }}
            >
              {config.sortOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {/* Score methodology link — persistent, replaces per-row links */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
        <Typography
          component="button"
          onClick={handleExplainerOpen}
          sx={{
            background: 'none',
            border: 'none',
            p: 0,
            cursor: 'pointer',
            color: 'text.disabled',
            fontSize: '0.75rem',
            textDecoration: 'underline',
            textDecorationColor: 'divider',
            '&:hover': { color: 'primary.main' },
          }}
        >
          ¿Cómo calculamos el FinMaple Score?
        </Typography>
      </Box>

      {/* Persona filters */}
      <PersonaFilterBar
        personas={config.personas}
        activePersonas={activePersonas}
        onToggle={handlePersonaToggle}
        onClear={handleClearPersonas}
      />

      {/* Product rows or empty state */}
      {filtered.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, bgcolor: '#F7F9FC', borderRadius: '16px', mt: 2 }}>
          <SearchOff sx={{ fontSize: 56, color: '#E6E9EE', mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 1 }}>
            {t('comparator.emptyTitle')}
          </Typography>
          <Typography variant="body2" sx={{ color: '#5F6B7A', mb: 3, maxWidth: 400, mx: 'auto' }}>
            {t('comparator.emptyDescription')}
          </Typography>
          <Button
            variant="outlined"
            onClick={handleClearPersonas}
            sx={{ textTransform: 'none', borderRadius: '10px', fontWeight: 600, borderColor: '#1E3A5F', color: '#1E3A5F' }}
          >
            {t('comparator.resetFilters')}
          </Button>
        </Box>
      ) : (
        paginatedItems.map((item, idx) => {
          const absoluteRank = (currentPage - 1) * ITEMS_PER_PAGE + idx + 1;
          return (
            <ProductRow
              key={item.id}
              item={item}
              rank={absoluteRank}
              config={config}
              category={category}
              selectedForCompare={selectedForCompare}
              onToggleCompare={toggleCompare}
              onViewDetail={handleOpenDetail}
              onExplainerClick={handleExplainerOpen}
              recommendationLine={absoluteRank === 1 ? recommendationLine : null}
            />
          );
        })
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, p) => setCurrentPage(p)}
            color="primary"
          />
        </Box>
      )}

      {/* Trust microline */}
      <TrustMicroline />

      {/* Compare tray (enhanced) */}
      <ComparatorTray
        selectedItems={selectedForCompare}
        nameField={config.nameField}
        onRemove={(item) => toggleCompare(item)}
        onClear={() => setSelectedForCompare([])}
        onCompare={handleRunCompare}
      />

      {/* Comparison modal */}
      <ProductComparisonModal
        open={showComparison}
        onClose={() => setShowComparison(false)}
        items={selectedForCompare}
        config={config}
        category={category}
      />

      {/* Detail modal */}
      <ProductDetailModal
        open={!!detailItem}
        onClose={() => setDetailItem(null)}
        item={detailItem}
        config={config}
        category={category}
        rank={detailItem ? filtered.findIndex((i) => i.id === detailItem.id) + 1 : null}
        onExplainerClick={handleExplainerOpen}
      />

      {/* Scoring explainer modal */}
      <ScoringExplainerModal open={showExplainer} onClose={() => setShowExplainer(false)} />
    </Box>
  );
};

/* ────────────────────── GLOBAL FRESHNESS LINE ────────────────────── */
const GlobalFreshnessLine = ({ freshness, loading }) => {
  const { t } = useTranslation();
  if (loading || !freshness?.label) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: 2,
        py: 1,
        px: 1.5,
        bgcolor: 'neutral.50',
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <CheckCircleIcon sx={{ fontSize: '1rem', color: 'success.main' }} />
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
        {t('comparator.dataUpdated', { date: freshness.label })}
      </Typography>
      <Tooltip
        title={t('comparator.verifiedTooltip')}
        arrow
        placement="top"
      >
        <InfoIcon sx={{ fontSize: '0.9rem', color: 'text.disabled', cursor: 'help' }} />
      </Tooltip>
    </Box>
  );
};

/* ── Helper: get best rate per term to highlight top values ── */
const getBestRates = (rateData, termKeys) => {
  const best = {};
  termKeys.forEach((tk) => {
    const entries = rateData?.[tk];
    if (Array.isArray(entries) && entries.length > 0) {
      best[tk] = Math.max(...entries.map((e) => e.rate_percent));
    }
  });
  return best;
};

/* ── Helper: provider initials for avatar fallback ── */
const getInitials = (name) => {
  if (!name) return '?';
  const words = name.replace(/\(.*?\)/g, '').trim().split(/\s+/);
  return words.length >= 2
    ? (words[0][0] + words[1][0]).toUpperCase()
    : name.slice(0, 2).toUpperCase();
};

/* ────────────────────── GIC TABLE SUB-COMPONENT ────────────────────── */
const GICTable = ({ data, rawSection, loading, config }) => {
  const { t } = useTranslation();
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  const termKeys = config.termKeys || [];
  const termLabels = config.termLabels || {};

  // Walk from rawSection (json.gics) into the sub-path (top_rates_feb_2026)
  const subParts = (config.dataPath || '').split('.').slice(1);
  let rateData = rawSection;
  for (const p of subParts) rateData = rateData?.[p];

  // Collect unique providers across all terms
  const providerSet = new Set();
  termKeys.forEach((tk) => {
    const entries = rateData?.[tk];
    if (Array.isArray(entries)) {
      entries.forEach((e) => providerSet.add(e.provider));
    }
  });
  const providers = Array.from(providerSet);

  // Build a lookup: provider → term → entry
  const lookup = {};
  termKeys.forEach((tk) => {
    const entries = rateData?.[tk];
    if (Array.isArray(entries)) {
      entries.forEach((e) => {
        if (!lookup[e.provider]) lookup[e.provider] = {};
        lookup[e.provider][tk] = e;
      });
    }
  });

  const bigBank = rateData?.big_bank_comparison;
  const bestRates = getBestRates(rateData, termKeys);

  return (
    <Box>
      {/* ── Rate table ── */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: '16px',
          border: '1px solid #E6E9EE',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ overflowX: 'auto' }}>
          <Box
            component="table"
            sx={{
              width: '100%',
              borderCollapse: 'separate',
              borderSpacing: 0,
              '& th': {
                fontSize: '12px',
                fontWeight: 700,
                color: '#5F6B7A',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                px: { xs: 1.5, md: 2 },
                py: 2,
                bgcolor: '#F7F9FC',
                borderBottom: '2px solid #E6E9EE',
                whiteSpace: 'nowrap',
                textAlign: 'center',
                '&:first-of-type': { textAlign: 'left' },
              },
              '& td': {
                px: { xs: 1.5, md: 2 },
                py: 2,
                borderBottom: '1px solid #F0F2F5',
                textAlign: 'center',
                verticalAlign: 'middle',
                '&:first-of-type': { textAlign: 'left' },
              },
              '& tbody tr': {
                transition: 'background-color 150ms ease',
                '&:hover': { bgcolor: 'rgba(30,58,95,0.03)' },
                '&:last-child td': { borderBottom: 'none' },
              },
            }}
          >
            <thead>
              <tr>
                <th>{t('comparator.thProvider')}</th>
                {termKeys.map((tk) => (
                  <th key={tk}>{termLabels[tk] || tk}</th>
                ))}
                <th>{t('comparator.thProtection')}</th>
                <th>{t('comparator.thMinDeposit')}</th>
              </tr>
            </thead>
            <tbody>
              {providers.map((prov) => {
                const provLogo = getLogoPath(prov);
                const anyEntry = Object.values(lookup[prov] || {})[0];
                return (
                  <tr key={prov}>
                    {/* Provider cell */}
                    <td>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: { xs: 120, sm: 160 } }}>
                        {provLogo ? (
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '10px',
                              bgcolor: '#FFFFFF',
                              border: '1px solid #E6E9EE',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              overflow: 'hidden',
                              p: '4px',
                            }}
                          >
                            <Box
                              component="img"
                              src={provLogo}
                              alt={prov}
                              sx={{ width: 28, height: 28, objectFit: 'contain' }}
                            />
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '10px',
                              bgcolor: '#1E3A5F',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#FFFFFF' }}>
                              {getInitials(prov)}
                            </Typography>
                          </Box>
                        )}
                        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1A', lineHeight: 1.3 }}>
                          {prov}
                        </Typography>
                      </Box>
                    </td>

                    {/* Rate cells */}
                    {termKeys.map((tk) => {
                      const entry = lookup[prov]?.[tk];
                      const isBest = entry && entry.rate_percent === bestRates[tk];
                      return (
                        <td key={tk}>
                          {entry ? (
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: '8px',
                                bgcolor: isBest ? '#E8F5E9' : 'transparent',
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: '14px',
                                  fontWeight: 700,
                                  color: isBest ? '#2E7D32' : '#1E3A5F',
                                }}
                              >
                                {entry.rate_percent.toFixed(2)}%
                              </Typography>
                            </Box>
                          ) : (
                            <Typography sx={{ fontSize: '14px', color: '#E6E9EE' }}>—</Typography>
                          )}
                        </td>
                      );
                    })}

                    {/* Protection cell */}
                    <td>
                      {anyEntry?.cdic === true ? (
                        <Chip
                          label="CDIC"
                          size="small"
                          sx={{
                            bgcolor: '#E8F5E9',
                            color: '#2E7D32',
                            fontWeight: 700,
                            fontSize: '12px',
                            height: 24,
                            borderRadius: '6px',
                          }}
                        />
                      ) : anyEntry?.provincial_insurance ? (
                        <Chip
                          label="Provincial"
                          size="small"
                          sx={{
                            bgcolor: '#FFF8E1',
                            color: '#7D5A00',
                            fontWeight: 700,
                            fontSize: '12px',
                            height: 24,
                            borderRadius: '6px',
                          }}
                        />
                      ) : (
                        <Typography sx={{ fontSize: '13px', color: '#5F6B7A' }}>—</Typography>
                      )}
                    </td>

                    {/* Min deposit cell */}
                    <td>
                      <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#1A1A1A' }}>
                        {anyEntry?.min_deposit != null
                          ? `$${anyEntry.min_deposit.toLocaleString('en-CA')}`
                          : '—'}
                      </Typography>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Box>
        </Box>
      </Paper>

      {/* ── Big 5 comparison strip ── */}
      {bigBank && (
        <Box
          sx={{
            mt: 3,
            p: { xs: 2, md: 3 },
            bgcolor: '#FFEBEE',
            borderRadius: '14px',
            border: '1px solid #FFCDD2',
          }}
        >
          <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#C62828', mb: 1.5 }}>
            {t('comparator.big5Comparison')}
          </Typography>
          <Box sx={{ display: 'flex', gap: { xs: 1, md: 2 }, flexWrap: 'wrap', alignItems: 'center' }}>
            {Object.entries(bigBank)
              .filter(([k]) => k !== 'note')
              .map(([k, v]) => {
                const bankName = k.replace('_1yr', '');
                const bankLogo = getLogoPath(bankName);
                return (
                  <Box
                    key={k}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      bgcolor: '#FFFFFF',
                      border: '1px solid #FFCDD2',
                      borderRadius: '10px',
                      px: 1.5,
                      py: 0.75,
                    }}
                  >
                    {bankLogo ? (
                      <Box
                        component="img"
                        src={bankLogo}
                        alt={bankName}
                        sx={{ width: 20, height: 20, objectFit: 'contain' }}
                      />
                    ) : (
                      <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#C62828' }}>
                        {bankName.slice(0, 3)}
                      </Typography>
                    )}
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#C62828' }}>
                      {bankName}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#5F6B7A' }}>
                      {v}%
                    </Typography>
                  </Box>
                );
              })}
          </Box>
          {bigBank.note && (
            <Typography sx={{ fontSize: '12px', color: '#5F6B7A', mt: 1.5, lineHeight: 1.4 }}>
              💡 {bigBank.note}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

/* ────────────────────── CARDS LAYOUT (Registered Accounts) ────────────────────── */
const CardsLayout = ({ items, loading, config, category, onViewDetail, detailItem, onCloseDetail, onExplainerClick }) => {
  const { t } = useTranslation();
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mb: 2 }}>
        {items.length} {config.countLabel}
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
        {items.map((item, idx) => (
          <Paper
            key={item.id}
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: '1px solid',
              borderColor: idx === 0 ? 'warning.main' : 'divider',
              bgcolor: idx === 0 ? 'neutral.25' : 'background.paper',
              transition: 'box-shadow 0.2s ease',
              '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.08)' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <UniversalScoreBadge item={item} onExplainerClick={onExplainerClick} />
              <Box>
                <Chip
                  label={item.type}
                  size="small"
                  sx={{ fontWeight: 600, bgcolor: 'neutral.100', color: 'primary.main', fontSize: '0.7rem', mb: 0.5 }}
                />
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.3 }}>
                  {item[config.nameField]}
                </Typography>
              </Box>
            </Box>

            {/* Tax treatment badge */}
            {item.tax_treatment && (
              <Chip
                label={item.tax_treatment.replace(/_/g, ' ')}
                size="small"
                sx={{ mb: 1.5, fontWeight: 600, bgcolor: 'warning.main', color: 'warning.contrastText', fontSize: '0.75rem' }}
              />
            )}

            {/* Contribution limit */}
            {item.contribution_limit_2026 != null && (
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main', mb: 0.5 }}>
                Límite 2026: {typeof item.contribution_limit_2026 === 'number' ? `$${item.contribution_limit_2026.toLocaleString('en-CA')}` : item.contribution_limit_2026}
              </Typography>
            )}

            {/* Description */}
            {item.content?.short_description_es && (
              <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6, mb: 1.5 }}>
                {item.content.short_description_es}
              </Typography>
            )}

            {/* Why bullets */}
            <WhyBullets item={item} rank={idx + 1} />

            <Button
              size="small"
              onClick={() => onViewDetail(item)}
              sx={{ textTransform: 'none', fontWeight: 600, color: 'primary.main', mt: 1 }}
            >
              {t('comparator.viewAllDetails')}
            </Button>
          </Paper>
        ))}
      </Box>

      {/* Detail modal */}
      <ProductDetailModal
        open={!!detailItem}
        onClose={onCloseDetail}
        item={detailItem}
        config={config}
        category={category}
        rank={detailItem ? items.findIndex((i) => i.id === detailItem.id) + 1 : null}
        onExplainerClick={onExplainerClick}
      />
    </Box>
  );
};

export default FinmapleComparator;
